import { useMutation, useQuery } from '@apollo/client';
import { ComputerDesktopIcon, EyeIcon, TrashIcon } from '@heroicons/react/20/solid';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../components/buttons/Button';
import Modal from '../../../components/modal/Modal';
import Table from '../../../components/table/Table';
import { useToast } from '../../../components/toast/ToastProvider';
import { Link, Subtitle, Title } from '../../../components/typography';
import { DELETE_DEVICE } from '../../../graphql/devices/mutations';
import { GET_DEVICE, GET_DEVICES } from '../../../graphql/devices/queries';
import Form from './sharedComponents/Form';

function Devices() {
  const { _id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data } = useQuery(GET_DEVICES);
  const [open, setOpen] = useState(false);
  const [deleteDevice] = useMutation(DELETE_DEVICE);
  const { data: device } = useQuery(GET_DEVICE, {
    variables: { _id }
  });

  const handleClick = async (id) => {
    const res = await deleteDevice({
      variables: { _id: id },
      refetchQueries: [{ query: GET_DEVICES }, 'GetDevices']
    });

    toast({
      title: res.data?.deleteDevice?.error ? 'Device not deleted' : 'Device deleted',
      description:
        res.data?.deleteDevice?.message ||
        `The device ${res.data?.deleteDevice?.name} has been deleted successfully.`,
      type: res.data?.deleteDevice?.error ? 'error' : 'success'
    });
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'name',
        Header: 'Name'
      },
      {
        accessor: 'createdBy',
        Header: 'Creator',
        Cell: ({ value }) => {
          return <Link href={`mailto:${value}`} text={value} />;
        }
      },
      {
        accessor: 'createdAt',
        Header: 'Date',
        Cell: ({ value }) => {
          return <>{dayjs(value).format('LLL')}</>;
        }
      },
      {
        accessor: 'actions',
        Header: '',
        Cell: ({ row }) => {
          return (
            <div className="flex items-center justify-center space-x-2">
              <Button
                type="button"
                color="text-red-500"
                Icon={TrashIcon}
                rounded
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  toast({
                    type: 'warning',
                    title: 'Delete devices',
                    autoClose: false,
                    description: 'Are you sure you want to delete this devices?',
                    onAccept: () => handleClick(row.original._id)
                  });
                }}
              />
              <Button
                type="button"
                color="text-sky-500"
                Icon={EyeIcon}
                rounded
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/employees/devices/${row.original._id}`);
                }}
              />
            </div>
          );
        },
        disableSortBy: true
      }
    ],
    []
  );

  useEffect(() => {
    if (_id) setOpen(true);
  }, [_id]);

  return (
    <>
      <Modal
        open={open}
        closeModal={() => setOpen(false)}
        title="Device Form"
        description="Use the following request form to create or edit a device."
        onExitComplete={() => {
          if (_id) {
            navigate('/employees/devices/');
          }
        }}
      >
        <Form onSuccess={() => setOpen(false)} data={device?.device} />
      </Modal>
      <Title text="Devices" />
      <Subtitle text="This section is intended for managing employees devices. Please use this section to create, read, update or delete a device." />
      <Table
        columns={columns}
        data={data?.devices || []}
        onRowClick={(row) => navigate(`/employees/devices/${row?._id}`)}
        toolbar={
          <div className="flex items-center">
            <Button
              label="New Device"
              Icon={ComputerDesktopIcon}
              backgroundColor="bg-green-500"
              onClick={() => setOpen(true)}
            />
          </div>
        }
      />
    </>
  );
}

export default Devices;
